/**
 * ScrollAnimationObserver - IntersectionObserver-based scroll animation triggering
 *
 * Observes elements with scroll animation classes and triggers them when visible.
 * Supports configurable thresholds, rootMargin, replay, and staggered animations.
 *
 * Usage:
 *   import ScrollAnimationObserver from '~/javascript/services/scroll_animation_observer'
 *
 *   // Initialize with defaults
 *   const observer = new ScrollAnimationObserver()
 *   observer.observe()
 *
 *   // Or with custom options
 *   const observer = new ScrollAnimationObserver({
 *     threshold: 0.2,
 *     rootMargin: '-50px',
 *     replay: true,
 *     staggerDelay: 100
 *   })
 */

const DEFAULT_OPTIONS = {
  // IntersectionObserver threshold (0-1)
  threshold: 0.1,

  // Root margin for triggering earlier/later
  rootMargin: '0px 0px -50px 0px',

  // Whether to replay animations when elements re-enter viewport
  replay: false,

  // Base delay in ms for staggered animations
  staggerDelay: 100,

  // Maximum stagger delay (prevents extremely long waits)
  maxStaggerDelay: 1000,

  // Selector for elements to observe
  selector: '[data-animate-on-scroll], .animate-on-scroll',

  // Class to add when element is visible
  visibleClass: 'is-visible',

  // Class to add when animation has completed (for one-time animations)
  animatedClass: 'has-animated',

  // Selector for staggerable children within an element
  staggerSelector: '[data-stagger]',

  // Respect prefers-reduced-motion
  respectReducedMotion: true
}

export default class ScrollAnimationObserver {
  /**
   * @param {Object} options Configuration options
   * @param {number} options.threshold - IntersectionObserver threshold (0-1)
   * @param {string} options.rootMargin - Root margin for triggering
   * @param {boolean} options.replay - Replay animations on re-enter
   * @param {number} options.staggerDelay - Base stagger delay in ms
   * @param {string} options.selector - Selector for elements to observe
   * @param {string} options.visibleClass - Class added when visible
   * @param {boolean} options.respectReducedMotion - Respect prefers-reduced-motion
   */
  constructor(options = {}) {
    this.options = { ...DEFAULT_OPTIONS, ...options }
    this.observer = null
    this.observedElements = new Set()
    this.reducedMotion = false

    // Check for reduced motion preference
    if (this.options.respectReducedMotion) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      // Listen for changes
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
        this.reducedMotion = e.matches
      })
    }
  }

  /**
   * Start observing elements
   * @param {Element|Document} root - Root element to search within (default: document)
   */
  observe(root = document) {
    if (this.observer) {
      this.disconnect()
    }

    // Create the IntersectionObserver
    this.observer = new IntersectionObserver(
      this.handleIntersection.bind(this),
      {
        threshold: this.options.threshold,
        rootMargin: this.options.rootMargin
      }
    )

    // Find and observe all matching elements
    const elements = root.querySelectorAll(this.options.selector)
    elements.forEach((element) => {
      this.observeElement(element)
    })

    return this
  }

  /**
   * Observe a single element
   * @param {Element} element - Element to observe
   */
  observeElement(element) {
    if (this.observedElements.has(element)) return

    // Set up staggered children if present
    this.setupStaggeredChildren(element)

    this.observer.observe(element)
    this.observedElements.add(element)
  }

  /**
   * Stop observing an element
   * @param {Element} element - Element to unobserve
   */
  unobserveElement(element) {
    if (!this.observedElements.has(element)) return

    this.observer.unobserve(element)
    this.observedElements.delete(element)
  }

  /**
   * Disconnect the observer and stop all observation
   */
  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    this.observedElements.clear()
  }

  /**
   * Handle intersection changes
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection(entries) {
    entries.forEach((entry) => {
      const element = entry.target

      if (entry.isIntersecting) {
        this.animateIn(element)
      } else if (this.options.replay) {
        this.animateOut(element)
      }
    })
  }

  /**
   * Trigger animation in
   * @param {Element} element
   */
  animateIn(element) {
    // Skip if already animated and not replaying
    if (!this.options.replay && element.classList.contains(this.options.animatedClass)) {
      return
    }

    // If reduced motion is preferred, show immediately without animation
    if (this.reducedMotion) {
      element.classList.add(this.options.visibleClass, this.options.animatedClass)
      element.style.opacity = '1'
      return
    }

    // Add visible class to trigger animation
    element.classList.add(this.options.visibleClass)

    // Animate staggered children
    this.animateStaggeredChildren(element)

    // Mark as animated after animation completes
    const animationDuration = this.getAnimationDuration(element)
    setTimeout(() => {
      element.classList.add(this.options.animatedClass)
    }, animationDuration)

    // Dispatch custom event
    element.dispatchEvent(new CustomEvent('scroll-animate-in', {
      bubbles: true,
      detail: { element }
    }))
  }

  /**
   * Reset animation (for replay)
   * @param {Element} element
   */
  animateOut(element) {
    element.classList.remove(this.options.visibleClass, this.options.animatedClass)

    // Reset staggered children
    const children = element.querySelectorAll(this.options.staggerSelector)
    children.forEach((child) => {
      child.classList.remove(this.options.visibleClass)
    })

    // Dispatch custom event
    element.dispatchEvent(new CustomEvent('scroll-animate-out', {
      bubbles: true,
      detail: { element }
    }))
  }

  /**
   * Set up staggered animation delays for children
   * @param {Element} element
   */
  setupStaggeredChildren(element) {
    const children = element.querySelectorAll(this.options.staggerSelector)

    children.forEach((child, index) => {
      // Check for custom stagger value in data attribute
      const customStagger = child.dataset.stagger
      let delay

      if (customStagger && !isNaN(parseInt(customStagger, 10))) {
        // Use custom stagger index from data attribute
        delay = parseInt(customStagger, 10) * this.options.staggerDelay
      } else {
        // Use DOM order for stagger
        delay = index * this.options.staggerDelay
      }

      // Cap at max delay
      delay = Math.min(delay, this.options.maxStaggerDelay)

      // Apply delay as CSS custom property
      child.style.setProperty('--stagger-delay', `${delay}ms`)
      child.style.animationDelay = `${delay}ms`
    })
  }

  /**
   * Animate staggered children
   * @param {Element} element
   */
  animateStaggeredChildren(element) {
    const children = element.querySelectorAll(this.options.staggerSelector)

    children.forEach((child, index) => {
      // Get the computed delay
      const delay = parseInt(child.style.animationDelay, 10) || (index * this.options.staggerDelay)

      setTimeout(() => {
        child.classList.add(this.options.visibleClass)
      }, delay)
    })
  }

  /**
   * Get the total animation duration for an element
   * @param {Element} element
   * @returns {number} Duration in ms
   */
  getAnimationDuration(element) {
    const computed = window.getComputedStyle(element)
    const duration = parseFloat(computed.animationDuration) || 0
    const delay = parseFloat(computed.animationDelay) || 0

    // Convert from seconds to ms
    return ((duration + delay) * 1000) || 500
  }

  /**
   * Manually trigger animation for an element
   * @param {Element} element
   */
  trigger(element) {
    this.animateIn(element)
  }

  /**
   * Run count-up animation for stat elements
   * Elements should have data-count-target and optionally data-count-suffix
   * @param {Element} container - Container element to search within
   * @param {boolean} instant - If true, show final value without animation
   */
  runCountUp(container = document, instant = false) {
    const countElements = container.querySelectorAll('[data-count-target]')
    if (!countElements.length) return

    // Skip animation if reduced motion is preferred
    if (this.reducedMotion) instant = true

    countElements.forEach((el) => {
      const target = parseFloat(el.dataset.countTarget) || 0
      const suffix = el.dataset.countSuffix || ''
      const duration = instant ? 0 : 2000
      let startTime = null

      // Add is-counting class for CSS animation
      el.classList.add('is-counting')

      const formatNumber = (num) => {
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
        return Math.round(num).toString()
      }

      if (instant || duration === 0) {
        el.textContent = formatNumber(target) + suffix
        return
      }

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp
        const progress = Math.min((timestamp - startTime) / duration, 1)
        const easeOut = 1 - (1 - progress) ** 3 // ease-out cubic
        const current = target * easeOut
        el.textContent = formatNumber(current) + suffix
        if (progress < 1) requestAnimationFrame(animate)
      }

      requestAnimationFrame(animate)
    })
  }

  /**
   * Reset an element's animation state
   * @param {Element} element
   */
  reset(element) {
    element.classList.remove(this.options.visibleClass, this.options.animatedClass)

    const children = element.querySelectorAll(this.options.staggerSelector)
    children.forEach((child) => {
      child.classList.remove(this.options.visibleClass)
    })
  }

  /**
   * Refresh observation (useful after DOM changes)
   * @param {Element|Document} root
   */
  refresh(root = document) {
    // Find new elements that aren't being observed
    const elements = root.querySelectorAll(this.options.selector)

    elements.forEach((element) => {
      if (!this.observedElements.has(element)) {
        this.observeElement(element)
      }
    })
  }
}

// Auto-initialize when DOM is ready (for non-module usage)
if (typeof window !== 'undefined') {
  window.ScrollAnimationObserver = ScrollAnimationObserver

  // Auto-init with data attribute
  document.addEventListener('DOMContentLoaded', () => {
    const autoInit = document.querySelector('[data-scroll-animation-auto]')
    if (autoInit) {
      const options = {}

      if (autoInit.dataset.scrollAnimationThreshold) {
        options.threshold = parseFloat(autoInit.dataset.scrollAnimationThreshold)
      }
      if (autoInit.dataset.scrollAnimationRootMargin) {
        options.rootMargin = autoInit.dataset.scrollAnimationRootMargin
      }
      if (autoInit.dataset.scrollAnimationReplay === 'true') {
        options.replay = true
      }
      if (autoInit.dataset.scrollAnimationStaggerDelay) {
        options.staggerDelay = parseInt(autoInit.dataset.scrollAnimationStaggerDelay, 10)
      }

      const observer = new ScrollAnimationObserver(options)
      observer.observe()

      // Store reference for external access
      window.scrollAnimationObserver = observer
    }
  })
}
